import { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { Button } from '../ui/button';

const MonthlyView = ({ api, currentWeekStart, onWeekSelect }) => {
  const [monthData, setMonthData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = parseISO(currentWeekStart);
    return { year: date.getFullYear(), month: date.getMonth() + 1 };
  });

  useEffect(() => {
    fetchMonthData();
  }, [currentMonth, api]);

  const fetchMonthData = async () => {
    if (!api) return;
    setLoading(true);
    try {
      const res = await api.get(`/timesheets/monthly/${currentMonth.year}/${currentMonth.month}`);
      setMonthData(res.data);
    } catch (error) {
      console.error('Failed to fetch monthly data:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      let newMonth = prev.month + direction;
      let newYear = prev.year;
      if (newMonth > 12) { newMonth = 1; newYear++; }
      else if (newMonth < 1) { newMonth = 12; newYear--; }
      return { year: newYear, month: newMonth };
    });
  };

  const monthLabel = format(new Date(currentMonth.year, currentMonth.month - 1), 'MMMM yyyy');

  const StatusBadge = ({ status }) => {
    const config = {
      draft: { label: 'Draft', class: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300' },
      submitted: { label: 'Submitted', class: 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300' },
      approved: { label: 'Approved', class: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300' },
      rejected: { label: 'Rejected', class: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300' }
    };
    const { label, class: className } = config[status] || config.draft;
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>{label}</span>;
  };

  if (loading) {
    return (
      <div className="premium-card p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="premium-card" data-testid="monthly-view">
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
        <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)} className="h-9 w-9 rounded-xl">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-lg font-semibold text-slate-900 dark:text-white">{monthLabel}</span>
        <Button variant="outline" size="icon" onClick={() => navigateMonth(1)} className="h-9 w-9 rounded-xl">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {monthData.map((week) => {
          const startDate = parseISO(week.week_start);
          const endDate = parseISO(week.week_end);
          return (
            <button
              key={week.week_start}
              onClick={() => onWeekSelect(week.week_start)}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-left"
              data-testid={`month-week-${week.week_start}`}
            >
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  {format(startDate, 'MMM d')} - {format(endDate, 'MMM d')}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm font-semibold text-slate-900 dark:text-white">
                  {(week.total_hours || 0).toFixed(1)}h
                </span>
                <StatusBadge status={week.status} />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MonthlyView;
